import { useCallback, useEffect, useRef, useState } from 'react';
import { parseNcaKeyInfoResponse, type DN } from './parseKeyInfoSubject';

export type { DN };

const storage = 'PKCS12';
const module = 'kz.gov.pki.knca.commonUtils';

const getKeyInfo = {
  module,
  method: 'getKeyInfo',
  args: [storage],
};

const getSignBase64Method = (data: string) => ({
  module,
  method: 'createCAdESFromBase64',
  args: [storage, 'SIGNATURE', data, false],
});

interface Message {
  result: {
    version: string;
  };
  code: string;
  responseObject: {
    certNotAfter?: string;
    subjectDn?: string;
  };
}

function useNcaLayer() {
  const socket = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [dn, setDn] = useState<DN>({} as DN);

  const parseCompanyData = useCallback(
    (responseObject: Message['responseObject']) => {
      if (!responseObject) {
        return;
      }

      const { dn: dnData, expired } = parseNcaKeyInfoResponse(responseObject);

      if (expired) {
        setIsExpired(true);
        return;
      }

      setIsExpired(false);

      if (dnData) {
        setDn(dnData);
      }
    },
    []
  );

  const onMessage = useCallback(
    (e: MessageEvent) => {
      const { code, responseObject }: Message = JSON.parse(e.data);

      if (!code || code !== '200') {
        return;
      }

      if (responseObject) {
        parseCompanyData(responseObject);
      }
    },
    [parseCompanyData]
  );

  const onError = useCallback((e: any) => {
    setConnected(false);
  }, []);

  const onOpen = useCallback((e: Event) => {
    if (e.type === 'open') {
      setConnected(true);
    }
  }, []);

  const connectToSocket = useCallback(() => {
    socket.current = new WebSocket('wss://127.0.0.1:13579');

    socket.current.onopen = onOpen;

    socket.current.onmessage = onMessage;

    socket.current.onerror = onError;
  }, [onError, onMessage, onOpen]);

  const chooseCertificate = useCallback(() => {
    if (!connected) {
      connectToSocket();
    }

    if (socket.current?.readyState === 1) {
      socket.current?.send(JSON.stringify(getKeyInfo));
    }
  }, [connectToSocket, connected]);

  const signBase64 = useCallback(
    (file: string) => {
      if (!connected) {
        connectToSocket();
      }

      if (socket.current?.readyState === 1) {
        socket.current?.send(JSON.stringify(getSignBase64Method(file)));
      }
    },
    [connectToSocket, connected]
  );

  useEffect(() => {
    connectToSocket();
  }, [connectToSocket]);

  return {
    connected,
    isExpired,
    chooseCertificate,
    dn,
    signBase64,
  };
}

export default useNcaLayer;
